/**
 * Microsoft Clarity loader + helpers.
 *
 * Clarity is a free product (no quotas) that records session replays,
 * click heatmaps, and JS errors. We use it to investigate exam-submission
 * complaints by literally watching what the student did in their browser.
 *
 * Usage:
 *   - Set REACT_APP_CLARITY_ID to your Clarity project id (10-char string
 *     from https://clarity.microsoft.com → Settings → Setup).
 *   - Call initClarity() once at app boot.
 *   - Call identifyClarityUser(user) whenever the auth user changes —
 *     this makes replays searchable by username/email in the dashboard.
 *   - Call setClarityTag('key', 'value') from feature code to tag the
 *     session (e.g. examId during the exam page) so you can filter to
 *     "all replays of exam X".
 *
 * In development we skip loading by default (no spam in your localhost
 * recordings) unless REACT_APP_CLARITY_ENABLE_DEV=true.
 */

let isLoaded = false;

/**
 * Inject the official Clarity bootstrap snippet into the page.
 * The snippet is copy-pasted verbatim from Clarity's onboarding wizard —
 * do NOT "clean it up": the minification is intentional and the loader
 * is what every Clarity install ships.
 */
export const initClarity = (
  projectId = process.env.REACT_APP_CLARITY_ID,
  { enableInDev = process.env.REACT_APP_CLARITY_ENABLE_DEV === "true" } = {}
) => {
  if (isLoaded) return;
  if (!projectId) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.log("[clarity] init skipped: REACT_APP_CLARITY_ID not set");
    }
    return;
  }
  if (process.env.NODE_ENV !== "production" && !enableInDev) {
    // eslint-disable-next-line no-console
    console.log("[clarity] init skipped in dev (set REACT_APP_CLARITY_ENABLE_DEV=true to enable)");
    return;
  }

  try {
    (function (c, l, a, r, i, t, y) {
      c[a] =
        c[a] ||
        function () {
          (c[a].q = c[a].q || []).push(arguments);
        };
      t = l.createElement(r);
      t.async = 1;
      t.src = "https://www.clarity.ms/tag/" + i;
      y = l.getElementsByTagName(r)[0];
      y.parentNode.insertBefore(t, y);
    })(window, document, "clarity", "script", projectId);
    isLoaded = true;
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.log("[clarity] init loaded snippet, projectId =", projectId);
    }
  } catch (e) {
    // Never let analytics crash the app.
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.log("[clarity] init failed", e);
    }
  }
};

const callClarity = (...args) => {
  if (typeof window === "undefined") return;
  const fn = window.clarity;
  if (typeof fn !== "function") {
    // Clarity script hasn't installed its queue stub yet (or it's disabled
    // in this env). Drop silently — re-firing on the next auth tick will
    // pick it up once the snippet has run.
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.log("[clarity] skipped (no window.clarity yet):", args);
    }
    return;
  }
  try {
    fn(...args);
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.log("[clarity] call", args);
    }
  } catch (_) {
    // ignored — never break the app for analytics
  }
};

/**
 * Make replays searchable by user. Call this whenever the auth user
 * changes (login, refresh-token rotation, role change).
 *
 * Clarity's `identify` API takes a stable id; we also push username/role
 * as custom tags so they show up as filterable columns in the dashboard.
 *
 * IMPORTANT: do NOT pass anything sensitive here (raw emails are fine
 * for an LMS but never tokens / passwords / phone numbers).
 */
export const identifyClarityUser = (user) => {
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.log("[clarity] identifyClarityUser called with:", user);
  }
  if (!user) return;
  // Some APIs serialize the Mongo id as `_id`, others as `id`. Accept either.
  const rawId = user._id || user.id || user.userId;
  if (!rawId) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.log("[clarity] identify aborted: no id field on user object");
    }
    return;
  }
  const dbUserId = String(rawId);
  // `identify(customId, customSessionId, customPageId, friendlyName)`:
  //   - customId   → hashed by Clarity, shown as the opaque "User ID"
  //                  (e.g. "qqp2jq"). Searchable via Filters → Custom user ID.
  //   - friendlyName → displayed verbatim in the "Custom user ID" row of
  //                  the Session info panel. This is what humans actually read,
  //                  so we put the DB id here too.
  callClarity("identify", dbUserId, undefined, undefined, dbUserId);
  // Mirror the DB id (and other useful fields) as plain custom tags so they
  // also show up as filterable columns under Filters → Custom tags.
  callClarity("set", "userId", dbUserId);
  if (user.username) callClarity("set", "username", String(user.username));
  if (user.email) callClarity("set", "email", String(user.email));
  if (user.role) callClarity("set", "role", String(user.role));
  if (user.classId) callClarity("set", "classId", String(user.classId));
};

/** Tag the current session with arbitrary key/value for filtering. */
export const setClarityTag = (key, value) => {
  if (!key) return;
  callClarity("set", String(key), value == null ? "" : String(value));
};

/**
 * Mark a notable event in the replay timeline. Shows up as a bookmark
 * on the recording — invaluable for debugging "show me what happened
 * right before the popup".
 */
export const trackClarityEvent = (name) => {
  if (!name) return;
  callClarity("event", String(name));
};

/**
 * Upgrade a session to "prioritized" so Clarity keeps the recording
 * even under sampling. Call this on the few high-signal events you
 * care about — e.g. when a student opens the failed-answers banner,
 * or when /finish fails. This guarantees the replay is available
 * later when a ticket comes in.
 */
export const upgradeClaritySession = (reason) => {
  callClarity("upgrade", reason || "manual");
};
